/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, tap } from 'rxjs';
import { BaseComponent } from '~console/app/~shared/abstract-components/base.component';
import {
  ABIDJAN_GPS_COORDINATES,
  CI_BOUNDS
} from '~console/app/~shared/constants/global.constants';
import { GpsCoordinates } from '~console/app/~shared/models/shared.models';
import { MapService } from '~console/app/~shared/services/map.service';
import { environment } from '~environments/environment';

@Component({
  selector: 'onboarding-form-map-dialog',
  standalone: false,
  templateUrl: './onboarding-form-map-dialog.component.html',
  styleUrl: './onboarding-form-map-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingFormMapDialogComponent
  extends BaseComponent
  implements OnInit
{
  private readonly _mapService = inject(MapService);
  private readonly _document = inject(DOCUMENT);

  mapContainer = viewChild<ElementRef<HTMLDivElement> | undefined>(
    'mapContainer'
  );

  searchInput = viewChild<ElementRef | undefined>('searchInput');
  infoContentElement!: HTMLElement;

  map: google.maps.Map | undefined;
  mapMarker: google.maps.marker.AdvancedMarkerElement | undefined;
  mapCenter: GpsCoordinates = ABIDJAN_GPS_COORDINATES;
  mapZoom = 18;
  mapMinZoom = 8;
  infoWindow: google.maps.InfoWindow | undefined;

  coordinates = signal<GpsCoordinates | undefined>(undefined);

  readonly dialogRef = inject(MatDialogRef<OnboardingFormMapDialogComponent>);
  readonly data = inject<GpsCoordinates>(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this._initMap();
  }

  confirmPosition(): void {
    const coords = this.coordinates();
    if (!coords) return;
    this.dialogRef.close(coords);
  }

  private _mountPacContainerOnceItExists(input: HTMLInputElement): void {
    const searchContainer = this._document.querySelector(
      '.onboarding-form-map-dialog__search-container'
    );
    if (!searchContainer) return;

    const attach = (pac: HTMLElement): void => {
      searchContainer.appendChild(pac);
      const rect = input.getBoundingClientRect();
      pac.style.position = 'absolute';
      pac.style.left = `${rect.left}px`;
      pac.style.top = `${rect.bottom}px`;
      pac.style.width = `${rect.width}px`;
      pac.style.zIndex = '1001';
    };

    const existing = this._document.querySelector(
      '.pac-container'
    ) as HTMLElement | null;
    if (existing) {
      attach(existing);
      return;
    }

    const obs = new MutationObserver(() => {
      const pac = this._document.querySelector(
        '.pac-container'
      ) as HTMLElement | null;
      if (pac) {
        attach(pac);
        obs.disconnect();
      }
    });
    obs.observe(this._document.body, { childList: true, subtree: true });
  }

  private _initMap(): void {
    this._mapService
      .getMapLibrary()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(({ Map, InfoWindow }) => {
          const mapContainer = this.mapContainer()?.nativeElement;
          if (!mapContainer) return;

          this.map = new Map(mapContainer, {
            center: this.mapCenter,
            zoom: this.mapZoom,
            minZoom: this.mapMinZoom,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            mapId: environment.googleMapId
          });

          this.infoWindow = new InfoWindow({});

          if (
            this.data &&
            !isNaN(this.data.lat) &&
            !isNaN(this.data.lng) &&
            this._isInIvoryCoast(this.data.lat, this.data.lng)
          ) {
            this._addMapMarker(this.data, true);
          } else {
            this._moveMarkerToUserLocation();
          }

          this._listenToMapClicks();
          this._initGeocoder();
        })
      )
      .subscribe();
  }

  private _isInIvoryCoast(lat: number, lng: number): boolean {
    return (
      lat >= CI_BOUNDS.south &&
      lat <= CI_BOUNDS.north &&
      lng >= CI_BOUNDS.west &&
      lng <= CI_BOUNDS.east
    );
  }

  private _addMapMarker(
    coordinates: GpsCoordinates,
    pickLocation = false
  ): void {
    if (!this.map || !this.mapContainer()) return;

    if (!this._isInIvoryCoast(coordinates.lat, coordinates.lng)) {
      this._toastService.open({
        content:
          "Veuillez sélectionner une adresse sur le territoire de la Côte d'Ivoire.",
        type: 'error'
      });
      return;
    }

    if (this.mapMarker) {
      if (pickLocation && (this.map.getZoom() || 0) < 18) {
        this.map.setZoom(18);
      }

      this.mapMarker.position = coordinates;
      this.map.panTo(coordinates);
      this._setCoordinates({ lat: coordinates.lat, lng: coordinates.lng });
      return;
    }
    this._setCoordinates({ lat: coordinates.lat, lng: coordinates.lng });

    this._mapService
      .getMarkerLibrary()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(({ AdvancedMarkerElement }) => {
          const mapContainer = this.mapContainer()?.nativeElement;
          if (!mapContainer) return;

          const markerImg = this._document.createElement('img');
          markerImg.src = '/assets/icons/location-pin.svg';

          this.mapMarker = new AdvancedMarkerElement({
            map: this.map,
            position: coordinates,
            gmpDraggable: true,
            content: markerImg
          });

          this.map?.panTo(coordinates);
          this.mapMarker.addListener('dragend', () => {
            const lngLat = this.mapMarker!
              .position as google.maps.LatLngLiteral;

            if (lngLat) {
              if (
                !this._isInIvoryCoast(
                  lngLat.lat as number,
                  lngLat.lng as number
                )
              ) {
                this._toastService.open({
                  content:
                    "Veuillez sélectionner une adresse sur le territoire de la Côte d'Ivoire.",
                  type: 'error'
                });
                const currentCoords = this.coordinates();
                if (currentCoords) {
                  this.mapMarker!.position = currentCoords;
                }
                return;
              }

              this.map?.panTo(lngLat);
              this.map?.setZoom(this.mapZoom);
              this._setCoordinates({
                lat: lngLat.lat as number,
                lng: lngLat.lng as number
              });
            }
          });
        })
      )
      .subscribe();
  }

  private _moveMarkerToUserLocation(): void {
    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude: lat, longitude: lng } = position.coords;
        if (this._isInIvoryCoast(lat, lng)) {
          this._addMapMarker({ lng, lat }, true);
        } else {
          this._addMapMarker(this.mapCenter, true);
        }
      },
      () => {
        this._addMapMarker(this.mapCenter, true);
      },
      {
        enableHighAccuracy: true
      }
    );
  }

  private _listenToMapClicks(): void {
    this.map?.addListener('click', (event: any) => {
      if (event?.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        this._addMapMarker({ lat, lng }, true);
      }
    });
  }

  private _setCoordinates(coords: { lat: number; lng: number }): void {
    this.coordinates.set(coords);
  }

  private _initGeocoder(): void {
    forkJoin({
      geocoding: this._mapService.getGeocodingLibrary(),
      places: this._mapService.getPlacesLibrary()
    })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(({ geocoding, places }) => {
          if (!this.map) return;
          this._loggerService.log('geocoding', geocoding);
          const inputElement = this.searchInput()?.nativeElement;
          if (!inputElement) {
            this._loggerService.log('input element not found');
            return;
          }

          const autoComplete = this._initAutocomplete(
            places,
            inputElement,
            this.map
          );
          this._mountPacContainerOnceItExists(inputElement);

          const geocoder = new geocoding.Geocoder();

          this._prepareInfoWindowContent();
          this._setupPlaceChangedListener(autoComplete, geocoder);
          // this._addSearchInputToMapControls(inputElement);
        })
      )
      .subscribe();
  }

  private _initAutocomplete(
    places: google.maps.PlacesLibrary,
    inputElement: HTMLInputElement,
    map: google.maps.Map
  ): google.maps.places.Autocomplete {
    const autoComplete = new places.Autocomplete(inputElement, {
      fields: ['place_id', 'geometry', 'name', 'formatted_address'],
      componentRestrictions: { country: 'ci' }
    });
    autoComplete.bindTo('bounds', map);
    return autoComplete;
  }

  private _setupPlaceChangedListener(
    autoComplete: google.maps.places.Autocomplete,
    geocoder: google.maps.Geocoder
  ): void {
    autoComplete.addListener('place_changed', () => {
      this.infoWindow?.close();
      const place = autoComplete.getPlace();

      if (!place.place_id) return;

      geocoder
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          this.map?.panTo(location);
          this.map?.setZoom(this.mapZoom);
          this._addMapMarker({ lat, lng });
          this._setCoordinates({ lat, lng });

          this._updateInfoWindowContent(place, results[0]);
          this.infoWindow?.open(this.map, this.mapMarker);
        })
        .catch(e => console.error('Geocoder failed due to: ' + e));
    });
  }

  private _addSearchInputToMapControls(inputElement: HTMLElement): void {
    this.map?.controls[google.maps.ControlPosition.TOP_CENTER].push(
      inputElement
    );
  }

  private _prepareInfoWindowContent(): void {
    this.infoContentElement = this._document.createElement('div');
    this.infoContentElement.id = 'infowindow-content';
    this.infoContentElement.innerHTML = `_
    <h3 id="place-name"></h3>
    <p id="place-address"></p>
    <p id="place-id"></p>
  `;
    this.infoWindow?.setContent(this.infoContentElement);
  }

  private _updateInfoWindowContent(
    place: google.maps.places.PlaceResult,
    result: google.maps.GeocoderResult
  ): void {
    (
      this.infoContentElement.querySelector('#place-name') as HTMLElement
    ).textContent = place.name || '';
    (
      this.infoContentElement.querySelector('#place-address') as HTMLElement
    ).textContent = result.formatted_address || '';
    (
      this.infoContentElement.querySelector('#place-id') as HTMLElement
    ).textContent = place.place_id || '';
  }
}
